<template>
  <div class="composition-page">
    <chartLoaders v-if="showLoaders == true"></chartLoaders>
    <screen-loader v-if="showLoaders == true"></screen-loader>

    <div v-else-if="timeoutMessage != ''" class="tabs-no-test-div timeout">
      <div class="text-center">
        {{ timeoutMessage }}
        <v-icon flat @click="loadItems()" color="blue-darken-3" icon="mdi-reload" size="large"></v-icon>
      </div>
    </div>

    <v-card-text class="chart-table-wrap px-0 pb-0" v-else-if="serverItems.length > 0 && showLoaders == false">
      <div v-if="hasVariablesToPlot" class="chart-wrap">
        <div v-for="(variable, index) in variables" :key="index" class="chart-column">
          <div class="modal-tab-div">
            <div class="tab-div">
              <p>Y Axis Scale:</p>
              <v-btn-toggle v-model="isYLog[variable]" class="axis-toggle setHeight" mandatory
                @update:modelValue="handleYScaleToggleChange(variable)">
                <v-btn :value="false" class="switch-btn" outlined>Log</v-btn>
                <v-btn :value="true" class="switch-btn" outlined>Linear</v-btn>
              </v-btn-toggle>
            </div>
          </div>
          <div class="chart-name-top">
            <h6>{{ getHeaderDisplayNameChartTitle(variable) }}</h6>
          </div>
          <div :ref="`chart-${index}`" class="main-chart"></div>

          <v-dialog v-model="isPreviewOpen[index]" class="multi-chart-preview-div dashboard-chart-preview composition">
            <v-card class="position-relative studio-card">
              <v-card-text>
                <div class="popup-top">
                  <div class="modal-tab-div-composition">
                    <div class="tab-div">
                      <p>Y Axis Scale:</p>
                      <v-btn-toggle v-model="isModalYLog[index]" class="axis-toggle setHeight" mandatory
                        @update:modelValue="handleModalYScaleToggleChange(index)">
                        <v-btn :value="true" class="switch-btn" outlined>Log</v-btn>
                        <v-btn :value="false" class="switch-btn" outlined>Linear</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                  <div class="chart-name-top w-100">
                    <h6>{{ getHeaderDisplayNameChartTitle(variable) }}</h6>
                  </div>
                </div>
                <div :ref="`modal-chart-${index}`" class="modal-chart-container"></div>
              </v-card-text>
            </v-card>
          </v-dialog>
        </div>
      </div>
      <div v-else-if="!fluidComponentReference" class="text-center pt-1 pb-5">
        <h4>
          Charts couldn't be plotted as fluid component reference is not
          available in this test.
        </h4>
      </div>
      <div v-else class="text-center pt-1 pb-5">
        <h4>No variables available for plotting.</h4>
      </div>
      <div class="table-wrap">
        <div class="table-title-div" v-if="serverItems.length > 0">
          <p class="table-title">Composition Analysis</p>
          <div class="csv-btn" @click="downloadTableCSV">
            <v-img class="pointer" :src="documentDownload" alt="csv download"></v-img>
          </div>
        </div>
        <v-data-table v-if="showHeaders() && serverItems.length > 0" :headers="visibleHeaders" :items="visibleItems"
          :total-items="totalItems" :items-per-page="itemsPerPage" :loading="loading" :search="search"
          :item-value="name" virtual-items fixed-header max-height="calc(100vh - 380px)">
          <template v-slot:headers>
            <tr class="main-header">
              <th class="sticky-header">Fluid Component Reference</th>
              <th v-for="(header, index) in visibleHeaders" :key="index" class="sticky-header">
                {{ header.name }}
              </th>
            </tr>
            <tr class="sub-header">
              <th>Unit</th>
              <th v-for="(header, index) in visibleHeaders" :key="index">
                {{ header.unit }}
              </th>
            </tr>
          </template>
          <template v-slot:body="{ items }">
            <tr v-for="(item, index) in items" :key="index">
              <td>{{ item.fluidComponentReference }}</td>
              <td v-for="(header, index) in visibleHeaders" :key="index">
                {{ displayValue(header.key, item[header.key]) }}
              </td>
            </tr>
          </template>
          <template v-slot:body.append>
            <tr>
              <td>Total</td>
              <td v-for="(header, index) in visibleHeaders" :key="index">
                <span v-if="header.key.includes('MassFraction') && toggleFraction">
                  {{
                    sums[header.key] ? truncateNumber(sums[header.key], 3) : "-"
                  }}
                </span>
                <span v-if="header.key.includes('MoleFraction') && !toggleFraction">
                  {{
                    sums[header.key] ? truncateNumber(sums[header.key], 3) : "-"
                  }}
                </span>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </v-card-text>
    <div v-else-if="
      (!serverItems || serverItems.length === 0) && showLoaders == false
    " class="tabs-no-test-div">
      <h4>No data available.</h4>
    </div>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import ChartLoaders from "../../Loaders/ChartLoaders.vue";
import ScreenLoader from "../../Loaders/ScreenLoader.vue";
import { getDisplayName, getSortOrder } from "../../../../utils/cache";
import api from "../../../../axiosInterceptor";

import downloadIcon from "../../../../assets/Svg/Chart/csv_chart.svg";
import camIcon from "../../../../assets/Svg/camIcon.svg";
import Expand from "../../../../assets/Svg/Chart/expand-btn.svg";
import { mapGetters } from "vuex";
import { chartGridColor } from "@/utils/chartTheme";

export default {
  name: "CompositionAnalysisTab",
  components: {
    ChartLoaders,
    ScreenLoader,
  },
  data: () => ({
    documentDownload: require("../../../../assets/Images/csv.svg"),
    cross: require("../../../../assets/Svg/Chart/chart-cross.svg"),
    itemsPerPage: 0,
    headers: [],
    serverItems: [],
    loading: true,
    totalItems: 0,
    variables: [],
    isPreviewOpen: Array().fill(false),
    chartData: Array().fill(null),
    cachedData: {},
    testID: "",
    testName: "",
    sampleID: "",
    fluidSampleID: "",
    fluidComponentReference: false,
    saturationPressure: "",
    saturationPressure_UOM: "",
    testTemperature: "",
    testTemperature_UOM: "",
    toggleFraction: true,
    showLoaders: true,
    hasVariablesToPlot: true,
    showExpandButton: Array().fill(false),
    timeoutMessage: "",
    sums: {},
    yAxisScales: {},
    isYLog: {},
    modalYAxisScales: {},
    isModalYLog: {},
    compositionAnalysisHeader: {}
  }),
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    visibleHeaders() {
      const headers = [];
      this.headers.forEach((header) => {
        if (
          header.key === "fluidComponentReference" ||
          (this.toggleFraction == true &&
            header.key.includes("MassFraction")) ||
          (this.toggleFraction == false && header.key.includes("MoleFraction"))
        ) {
          headers.push(header);
        }
      });
      return headers;
    },

    visibleItems() {
      return this.serverItems.map((item) => {
        const newItem = {};
        Object.keys(item).forEach((key) => {
          if (
            key === "fluidComponentReference" ||
            (this.toggleFraction == true && key.includes("MassFraction")) ||
            (this.toggleFraction == false && key.includes("MoleFraction"))
          ) {
            newItem[key] = item[key];
          }
        });
        return newItem;
      });
    },
  },
  watch: {
    serverItems: {
      handler(newItems) {
        this.calculateSums(); // Call calculateSums when serverItems change
      },
      deep: true,
    },
    $route(to, from) {
      this.chartPlotted();
    },
    toggleFraction(newVal, oldVal) {
      this.chartPlotted();
      this.calculateSums();
    },
    isDarkMode() {
      this.chartPlotted();
    }
  },
  methods: {
    handleYScaleToggleChange(variable) {
      if (this.yAxisScales[variable] === undefined) {
        this.yAxisScales[variable] = 'linear';
      }

      this.yAxisScales[variable] = this.isYLog[variable] ? "linear" : "log";

      const chartIndex = this.variables.indexOf(variable);

      this.modalYAxisScales[chartIndex] = this.yAxisScales[variable];
      if (this.modalYAxisScales[chartIndex] === "linear") {
        this.isModalYLog[chartIndex] = true;
      }
      else {
        this.isModalYLog[chartIndex] = false;
      }
      if (this.cachedComposition) {
        this.plotChartForVariable(variable, chartIndex, this.cachedComposition);
      }
    },
    handleModalYScaleToggleChange(index) {
      if (this.modalYAxisScales[index] === undefined) {
        this.modalYAxisScales[index] = 'log';
      }

      this.modalYAxisScales[index] = this.isModalYLog[index] ? "log" : "linear";

      this.plotModalChart(index);
    },
    truncateNumber(value, decimals) {
      const factor = Math.pow(10, decimals);
      const truncatedValue = Math.floor(value * factor) / factor;
      return truncatedValue.toFixed(decimals);
    },
    calculateSums() {
      const sums = {};

      this.visibleHeaders.forEach((header) => {
        if (header.key.includes("MassFraction") && this.toggleFraction) {
          sums[header.key] = this.serverItems.reduce((sum, item) => {
            return sum + (parseFloat(item[header.key]) || 0);
          }, 0);
        } else if (
          header.key.includes("MoleFraction") &&
          !this.toggleFraction
        ) {
          sums[header.key] = this.serverItems.reduce((sum, item) => {
            return sum + (parseFloat(item[header.key]) || 0);
          }, 0);
        }
      });

      this.sums = sums;
    },
    async toggleFractionFunction() {
      this.serverItems = [];
      if (this.loading == true && this.serverItems.length == 0) {
        this.showLoaders = true;
      }
      await this.chartPlotted();
      this.$emit("toggle-fraction", this.toggleFraction);
    },
    async plotCharts(variablesToPlot, ccetest) {
      variablesToPlot.forEach((variable, index) => {
        this.plotChartForVariable(variable, index, ccetest);
      });
    },
    showHeaders() {
      return this.toggleFraction == true || this.toggleFraction == false;
    },
    async loadItems() {
      var sampleId = this.$route.params.sampleId;
      var compositionId = this.$route.params.id;
      this.loading = true;
      this.showLoaders = true;
      this.timeoutMessage = "";

      try {
        const sortOrder = await getSortOrder("compositiontests", "composition_analysis", this.toggleFraction ? "table_for_mass" : "table_for_mole");
        if (!sortOrder) {
          throw new Error("Sort order not found in cache.");
        }

        let response = await api.get(
          `public/api/samples/${sampleId}/compositiontests/${compositionId}`
        );

        if (
          response &&
          response.data &&
          (response.data.statusCode === 200 || response.data.statusCode === 201)
        ) {
          this.cachedComposition = response.data.data.compositiontests;
          this.serverItems =
            response.data.data.compositiontests &&
              response.data.data.compositiontests.composition
              ? response.data.data.compositiontests.composition
              : [];
          this.totalItems = this.serverItems.length;
          this.compositionAnalysisHeader = response.data.data.pageData.compositionAnalysisHeader;
          this.sendDataToParent();

          this.fluidComponentReference = this.serverItems.some((item) =>
            item.hasOwnProperty("fluidComponentReference")
          );
          const availableKeys = Object.keys(this.serverItems[0] || {}).map((key) =>
            key.replace("_UOM", "")
          );

          this.variables = sortOrder.filter((key) => {
            const isMass = this.toggleFraction === true && key.includes("MassFraction");
            const isMole = this.toggleFraction === false && key.includes("MoleFraction");
            const isValidType = key !== "fluidComponentReference" && key !== "stepNumber" && (isMass || isMole);
            return isValidType && availableKeys.includes(key);
          });

          this.serverItems = this.serverItems.filter(
            (item) => item.fluidComponentReference !== "H2"
          );

          const allUniqueKeys = Array.from(new Set(this.variables));
          const displayNames = [];
          const digitsArray = [];

          await Promise.all(
            allUniqueKeys.map(async (key) => {
              const fullKey = `composition${key}`; // Concatenate at start
              const capitalizedKey = fullKey.charAt(0) + fullKey.slice(1);
              const { displayName, digits } = await getDisplayName("compositiontests", capitalizedKey);
              displayNames.push(displayName);
              digitsArray.push(digits);
            })
          );



          this.headers = allUniqueKeys.map((key, index) => {
            const uom = this.serverItems.find((item) => item[key + "_UOM"]);
            const name = displayNames[index];
            let unit = uom ? uom[key + "_UOM"] : "-";
            if (!unit || unit === "data-not-available") {
              unit = "-";
            }
            const digits = digitsArray[index] || 0;
            return {
              key: key,
              name: name,
              unit: unit,
              digits: digits,
            };
          });
          this.calculateSums();
          return this.cachedComposition;
        } else if (response && response.data && response.data.message) {
          this.timeoutMessage = response.data.message;
        } else {
          this.timeoutMessage = "Error in fetching data";
          this.sendDataToParent();
        }

      } catch (error) {
        this.timeoutMessage = error.message || "Error in fetching data";
        this.sendDataToParent();
      } finally {
        this.loading = false;
        this.showLoaders = false;
      }
    },
    async plotChartForVariable(variable, chartIndex, composition) {
      if (!composition) {
        return;
      }

      const header = this.headers.find(header => header.key === variable);
      const digits = header ? header.digits : 0;
      const axisType = this.yAxisScales[variable] || "log";

      const filteredComposition = composition.composition
        .filter(
          step =>
            step.fluidComponentReference !== 'H2' &&
            step.fluidComponentReference &&
            step[variable] !== undefined &&
            step[variable] !== null
        );

      const fluidComponents = filteredComposition.map(step => step.fluidComponentReference);
      const values = filteredComposition.map(step => {
        const numericValue = Number(step[variable]);
        if (!Number.isFinite(numericValue)) {
          return null;
        }
        if (axisType === "log" && numericValue <= 0) {
          return null;
        }
        return numericValue;
      });
      const logTickConfig = axisType === "log" ? this.getLogTickConfig(values) : {};

      const trace = {
        x: fluidComponents,
        y: values,
        type: "scatter",
        mode: "lines+markers",
        name: variable,
        line: {
          color: "#2199A8",
          width: 2
        },
        marker: {
          color: "#2199A8",
          size: 8,
          line: {
            color: "#2199A8",
            width: 1
          }
        },
        connectgaps: false,
        hovertemplate: `%{x}<br>%{y:.${digits}f}<extra></extra>`
      };

      const layout = {
        xaxis: {
          title: {
            text: "Component",
          },
          tickangle: -45,
          tickfont: {
            size: 10,
          },
          automargin: true,
          gridcolor: chartGridColor(this.isDarkMode), 
        },
        yaxis: {
          title: {
            text: this.getHeaderDisplayName(variable),
            standoff: 20
          },
          type: axisType,
          ...logTickConfig,
          automargin: true,
          gridcolor: chartGridColor(this.isDarkMode), 
        },
        margin: {
          l: 50,
          r: 0,
          b: 120
        },
      };
      const config = {
        responsive: true,
        displaylogo: false,
        displayModeBar: true,
        modeBarButtonsToRemove: [
          "lasso2d",
          "select2d",
          "pan2d",
          "resetscale",
          "zoom",
          "toImage",
        ],
        modeBarButtonsToAdd: [
          {
            name: "Expand",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${Expand}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.openModal.bind(this, chartIndex),
          },
          {
            name: "Download CSV",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${downloadIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.handleDownloadCSV.bind(this, chartIndex),
          },
          {
            name: "Download plot as a png",
            icon: {
              svg: `
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                  <image href="${camIcon}" width="24" height="24"/>
                </svg>
              `,
            },
            click: this.handleDownloadPNG.bind(this, chartIndex),
          },
        ],
      };

      this.chartData[chartIndex] = {
        trace: trace,
        layout: layout,
        config: config,
      };

      const chartRef = this.$refs["chart-" + chartIndex];
      if (chartRef && chartRef.length > 0) {
        const chartElement = chartRef[0];
        Plotly.newPlot(chartElement, [trace], layout, config);
      } else {
        return;
      }

      this.showExpandButton.splice(chartIndex, 1, true);
    },
    getHeaderDisplayName(variable) {
      const header = this.headers.find((header) => header.key === variable);
      if (header) {
        return header.unit !== "-"
          ? `${header.name} (${header.unit})`
          : header.name;
      }
      return variable;
    },
    getHeaderDisplayNameChartTitle(variable) {
      const header = this.headers.find(header => header.key === variable);
      if (header) {
        return header.name;
      }
      return variable;
    },
    getLogTickConfig(values) {
      const positiveValues = values.filter(
        (value) => Number.isFinite(value) && value > 0
      );
      if (!positiveValues.length) {
        return {};
      }

      const minValue = Math.min(...positiveValues);
      const maxValue = Math.max(...positiveValues);
      const minExponent = Math.floor(Math.log10(minValue));
      const maxExponent = Math.ceil(Math.log10(maxValue));
      const tickvals = [];
      const ticktext = [];

      for (let exponent = minExponent; exponent <= maxExponent; exponent += 1) {
        const tickValue = Math.pow(10, exponent);
        tickvals.push(tickValue);
        ticktext.push(this.formatLogTick(tickValue));
      }

      return {
        tickmode: "array",
        tickvals,
        ticktext,
      };
    },
    formatLogTick(value) {
      if (!Number.isFinite(value) || value <= 0) {
        return "";
      }
      if (value >= 1) {
        return value.toLocaleString("en-US", {
          useGrouping: false,
          maximumFractionDigits: 6,
        });
      }
      const decimals = Math.min(12, Math.max(1, Math.ceil(-Math.log10(value))));
      return value.toFixed(decimals).replace(/\.?0+$/, "");
    },
    displayValue(key, value) {
      const header = this.headers.find((header) => header.key === key);
      const digits = header ? header.digits : 0;

      if (value !== null && value !== undefined) {
        if (typeof value === 'string') {
          return value;
        } else {
          const numValue = Number(value);
          if (!isNaN(numValue)) {
            return digits > 0 ? numValue.toFixed(digits) : value;
          }
        }
      }
      return "-";
    },
    handleDownloadCSV(chartIndex) {
      this.downloadChartCSV(chartIndex);
    },
    downloadChartCSV(index) {
      const chartData = this.chartData[index];

      if (chartData) {
        const fileName = `${chartData.layout.yaxis.title.text} vs ${chartData.layout.xaxis.title.text} data.csv`;

        const csvHeader = [
          chartData.layout.xaxis.title.text,
          chartData.layout.yaxis.title.text,
        ];

        const csvRows = chartData.trace.x.map((xValue, i) => [
          xValue,
          chartData.trace.y[i],
        ]);

        const csvContent = [
          csvHeader.join(","),
          ...csvRows.map((row) => row.join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });

        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          console.error(
            "Your browser does not support downloading files programmatically. Please try a different browser."
          );
        }
      }
    },

    handleDownloadPNG(chartIndex) {
      this.downloadChartPNG(chartIndex);
    },

    downloadChartPNG(index) {
      const chartElement = this.$refs[`chart-${index}`];
      if (chartElement && chartElement[0]) {
        const chartData = this.chartData[index];
        const fileName = chartData ? 
          `${chartData.layout.yaxis.title.text} vs ${chartData.layout.xaxis.title.text}` :
          `Chart ${index}`;

        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        document.body.appendChild(tempDiv);

        const layoutWithTitle = { ...chartData.layout };
        layoutWithTitle.title = {
          text: fileName,
          font: {
            size: 16,
            color: '#333'
          },
          x: 0.5,
          y: 0.95
        };

        Plotly.newPlot(tempDiv, [chartData.trace], layoutWithTitle, {
          displayModeBar: false,
          responsive: true
        }).then(() => {
          Plotly.downloadImage(tempDiv, {
            format: 'png',
            filename: fileName || 'chart',
            height: 600,
            width: 800
          }).then(() => {
            document.body.removeChild(tempDiv);
          });
        });
      }
    },
    openModal(index) {
      this.isPreviewOpen[index] = true;
      this.$nextTick(() => {
        this.plotModalChart(index);
      });
    },
    closeModal(index) {
      this.isPreviewOpen[index] = false;
    },
    plotModalChart(index) {
      const chartData = this.chartData[index];
      if (chartData) {
        const modalChartRef = this.$refs[`modal-chart-${index}`][0];
        
        const modalLayout = { ...chartData.layout };
        modalLayout.yaxis = {
          ...modalLayout.yaxis,
          type: this.modalYAxisScales[index] || "log"
        };
        
        Plotly.Plots.resize(modalChartRef);
        Plotly.newPlot(
          modalChartRef,
          [chartData.trace],
          modalLayout,
          chartData.config
        );
      }
    },
    async sendDataToParent() {
      const sortOrder = await getSortOrder("compositiontests", "composition_analysis", "header");

      const headerData = [];

      if (this.compositionAnalysisHeader && Array.isArray(this.compositionAnalysisHeader)) {
        for (const item of this.compositionAnalysisHeader) {
          const key = item.key;
          const value = item.value;
          
          const [valueOnly, unit] = typeof value === 'string' ? value.split(' ') : [value, null];

          const capitalizedKey = key.charAt(0) + key.slice(1);

          let displayName, digits;
          try {
            const result = await getDisplayName("compositiontests", capitalizedKey);
            displayName = result.displayName;
            digits = result.digits;
          } catch (e) {
            displayName = undefined;
            digits = undefined;
          }

          if (!displayName) {
            displayName = key;
          }

          headerData.push({
            originalKey: key,
            value: valueOnly,
            displayName: displayName,
            digits: digits,
            unit: unit || null
          });
        }
      }

      this.$emit("data-updated", {
        compositionAnalysisHeader: headerData,
        sortOrder: sortOrder,
        originalData: this.compositionAnalysisHeader
      });
    },
    downloadTableCSV() {
      if (this.serverItems && this.serverItems.length > 0) {
        const fileName = `composition_analysis_table.csv`;

        const csvHeader = this.headers.map((header) => header.name);
        const csvUnits = this.headers.map((header) => header.unit);

        const csvRows = this.serverItems.map((item) =>
          [item.fluidComponentReference, ...this.headers.map((header) => item[header.key])]
        );

        const csvContent = [
          ["Fluid Component Reference", ...csvHeader].join(","),
          ["Unit", "-", ...csvUnits].join(","), // Unit row includes "-" for the first column
          ...csvRows.map((row) => row.join(",")),
        ].join("\n");

        const blob = new Blob([csvContent], {
          type: "text/csv;charset=utf-8;",
        });

        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error("No data available in the table to download.");
      }
    },
    chartPlotted() {
      this.loadItems().then((ccetest) => {
        const variablesToPlot = [];
        if (this.toggleFraction) {
          variablesToPlot.push(
            "vapor_MassFraction",
            "liquid_MassFraction",
            "overall_MassFraction"
          );
        } else {
          variablesToPlot.push(
            "vapor_MoleFraction",
            "liquid_MoleFraction",
            "overall_MoleFraction"
          );
        }

        const filteredVariablesToPlot = variablesToPlot.filter((variable) => {
          return ccetest?.composition?.some(
            (item) => item[variable] !== undefined && item[variable] !== null
          );
        });

        this.hasVariablesToPlot = filteredVariablesToPlot.length > 0;
        if (
          JSON.stringify(filteredVariablesToPlot) !==
          JSON.stringify(this.variablesToPlot)
        ) {
          this.variablesToPlot = filteredVariablesToPlot;
        }

        filteredVariablesToPlot.forEach((variable, index) => {
          this.isYLog[variable] = false;
          this.isModalYLog[index] = false;
          this.plotChartForVariable(variable, index, this.cachedComposition);
        });
      });
    },
  },
  mounted() {
    this.chartPlotted();
  },
};
</script>